import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Settings, ToggleLeft, ToggleRight, Save, Sliders, BarChart3, Users as UsersIcon, Shield, AlertTriangle, RefreshCw, Radio, Plus, Zap, Trash2 } from "lucide-react";

const COMP_KEYS = [
  { key: "main_league", label: "Main Prediction League" },
  { key: "survivor", label: "Survivor Challenge" },
  { key: "bracket", label: "Knockout Bracket" },
  { key: "bracket_ko", label: "Bracket KO" },
  { key: "fantasy", label: "Fantasy XI" },
  { key: "streak", label: "Prediction Streak" },
  { key: "bonus_questions", label: "Bonus Questions" },
  { key: "chat", label: "Chat Room" },
];

function EloRatings({ tournamentId, teams, reloadTeams }) {
  const list = useMemo(
    () => Object.values(teams).filter((t) => t.code !== "TBD").sort((a, b) => (b.elo || 0) - (a.elo || 0) || (a.name || "").localeCompare(b.name || "")),
    [teams]
  );
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const val = (t) => (draft[t.team_id] !== undefined ? draft[t.team_id] : (t.elo ?? ""));
  const missing = list.filter((t) => !t.elo).length;

  const autoApply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tournaments/${tournamentId}/elo`, {});
      toast.success(`Applied Elo to ${data.updated} clubs${data.unmatched?.length ? ` · ${data.unmatched.length} unmatched` : ""}`);
      setDraft({});
      reloadTeams();
    } catch (e) { toast.error("Failed to apply Elo"); } finally { setBusy(false); }
  };
  const saveManual = async () => {
    const ratings = {};
    Object.entries(draft).forEach(([id, v]) => { const n = parseInt(v, 10); if (!Number.isNaN(n)) ratings[id] = n; });
    if (!Object.keys(ratings).length) return;
    setBusy(true);
    try {
      await api.post(`/admin/tournaments/${tournamentId}/elo`, { ratings });
      toast.success("Elo ratings saved");
      setDraft({});
      reloadTeams();
    } catch (e) { toast.error("Failed to save Elo"); } finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-5" data-testid="elo-ratings-panel">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-purple-300" />
          <h3 className="font-display text-lg uppercase tracking-wide">Elo Ratings</h3>
          {missing > 0 && <span className="text-[11px] text-primary-accent">{missing} missing</span>}
        </div>
        <div className="flex gap-2">
          <button data-testid="elo-auto-btn" onClick={autoApply} disabled={busy}
            className="border border-purple-400 text-purple-300 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-purple-500/15 disabled:opacity-50">Auto-apply Built-in</button>
          {Object.keys(draft).length > 0 && (
            <button data-testid="elo-save-btn" onClick={saveManual} disabled={busy}
              className="bg-primary-accent text-white px-3 py-1.5 text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50">Save Edits</button>
          )}
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Drives the underdog bonus. "Auto-apply" matches the built-in Premier League table by club name; edit any value (e.g. promoted clubs) and Save. Changing ratings re-scores settled games.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
        {list.map((t) => (
          <div key={t.team_id} className={`flex items-center justify-between gap-2 border px-2 py-1.5 ${t.elo ? "border-white/10" : "border-primary-accent/40"}`}>
            <span className="font-display text-sm tracking-wider truncate" title={t.name}>{t.code || t.name}</span>
            <input
              data-testid={`elo-input-${t.team_id}`}
              type="number" value={val(t)} placeholder="—"
              onChange={(e) => setDraft((d) => ({ ...d, [t.team_id]: e.target.value }))}
              className="w-16 bg-zinc-950 border border-zinc-800 px-2 py-1 text-right font-display text-sm focus:border-primary-accent focus:outline-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchdayGoLive({ live, onChange, matchdays }) {
  const mds = (matchdays && matchdays.length ? matchdays : [1, 2, 3]);
  const isOn = (md) => live.includes(md);
  const toggle = (md) => onChange(isOn(md) ? live.filter((x) => x !== md) : [...live, md].sort((a, b) => a - b));
  const allOn = mds.every((md) => isOn(md));
  return (
    <div className="surface-card p-5" data-testid="matchday-golive">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-primary-accent" />
          <h3 className="font-display text-lg uppercase tracking-wide">Matchday Go-Live</h3>
        </div>
        {mds.length > 6 && (
          <button
            data-testid="golive-toggle-all"
            onClick={() => onChange(allOn ? [] : [...mds])}
            className="text-[11px] uppercase tracking-widest border border-zinc-700 px-2 py-1 hover:border-primary-accent"
          >
            {allOn ? "Disable All" : "Enable All"}
          </button>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Open matchdays/gameweeks for Predictions &amp; Survivor. Players only see enabled matchdays.
      </p>
      <div className={`grid gap-2 ${mds.length > 6 ? "grid-cols-4 md:grid-cols-6 max-h-72 overflow-y-auto pr-1" : "grid-cols-3"}`}>
        {mds.map((md) => (
          <button
            key={md}
            data-testid={`golive-md-${md}`}
            onClick={() => toggle(md)}
            className={`px-2 py-2.5 border text-xs uppercase tracking-widest flex flex-col items-center justify-center gap-1 transition-colors ${
              isOn(md) ? "bg-green-500/15 border-green-500/50 text-green-400" : "border-zinc-700 text-zinc-400 hover:border-white/40"
            }`}
          >
            {isOn(md) ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            <span className="font-display text-base">GW{md}</span>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-zinc-500 mt-3">Matchdays also auto-open within 48h of their first kickoff.</div>
    </div>
  );
}

export default function Admin() {
  const { tournaments, active, refresh, setActive } = useTournament();
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(null);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [newTour, setNewTour] = useState({ name: "", sport: "football", format: "tournament", short_code: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const allMatchdays = useMemo(
    () => [...new Set(matches.filter((m) => m.stage === "group" && m.matchday != null).map((m) => m.matchday))].sort((a, b) => a - b),
    [matches]
  );

  const isLeague = editing?.format === "league";
  const matchBuckets = useMemo(() => {
    const b = [];
    allMatchdays.forEach((md) => b.push({ key: `md:${md}`, label: `${isLeague ? "GW" : "MD"} ${md}`, match: (m) => m.stage === "group" && m.matchday === md }));
    const stages = [...new Set(matches.filter((m) => m.stage !== "group").map((m) => m.stage))];
    stages.forEach((s) => b.push({ key: `stage:${s}`, label: (s || "").replace(/_/g, " ").toUpperCase(), match: (m) => m.stage === s }));
    return b;
  }, [allMatchdays, matches, isLeague]);
  const [matchView, setMatchView] = useState("");
  useEffect(() => {
    if (matchBuckets.length && !matchBuckets.find((b) => b.key === matchView)) {
      const live = editing?.config?.live_matchdays?.[0];
      setMatchView((live && matchBuckets.find((b) => b.key === `md:${live}`)?.key) || matchBuckets[0].key);
    }
  }, [matchBuckets]);
  const visibleMatches = useMemo(() => {
    const b = matchBuckets.find((x) => x.key === matchView);
    return b ? matches.filter(b.match) : [];
  }, [matches, matchBuckets, matchView]);

  useEffect(() => { if (active) setEditing(JSON.parse(JSON.stringify(active))); }, [active?.tournament_id]);

  useEffect(() => {
    if (!active) return;
    const tid = active.tournament_id;
    api.get(`/tournaments/${tid}/matches`).then((r) => setMatches(r.data));
    api.get(`/tournaments/${tid}/teams`).then((r) => setTeams(Object.fromEntries(r.data.map((t) => [t.team_id, t]))));
    api.get(`/admin/analytics/${tid}`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [active]);

  const reloadTeams = () => {
    if (!active) return;
    api.get(`/tournaments/${active.tournament_id}/teams`).then((r) => setTeams(Object.fromEntries(r.data.map((t) => [t.team_id, t]))));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await api.delete(`/admin/tournaments/${deleteTarget.tournament_id}`);
      toast.success(`Deleted "${data.deleted_tournament}"`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete tournament");
    }
  };

  const toggleArchive = async (t) => {
    const ending = t.status !== "archived";
    if (ending && !window.confirm(`End "${t.name}"? It becomes read-only and its overall champion is crowned in the Roll of Honour.`)) return;
    try {
      const { data } = await api.post(`/admin/tournaments/${t.tournament_id}/${ending ? "archive" : "reactivate"}`);
      if (ending) toast.success(data.champion ? `Ended — champion: ${data.champion.name} (${data.champion.points} pts)` : "Tournament ended");
      else toast.success("Tournament reactivated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const toggleComp = (key) => {
    setEditing((e) => ({ ...e, competitions: { ...e.competitions, [key]: !e.competitions[key] } }));
  };

  const updateCfg = (k, v) => {
    setEditing((e) => ({ ...e, config: { ...e.config, [k]: v } }));
  };

  const saveTournament = async () => {
    try {
      const { data } = await api.patch(`/tournaments/${editing.tournament_id}`, {
        name: editing.name,
        competitions: editing.competitions,
        config: editing.config,
        status: editing.status,
      });
      await refresh();
      if (data?.recalculated) {
        toast.success(`Scoring updated — ${data.recalculated.predictions_recalculated} predictions recalculated across ${data.recalculated.completed_matches} matches`);
      } else {
        toast.success("Tournament updated");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setMatchScore = async (match_id, h, a, penaltyWinner = null) => {
    try {
      await api.patch(`/matches/${match_id}/score`, { home_score: h, away_score: a, penalty_winner: penaltyWinner });
      const { data } = await api.get(`/tournaments/${active.tournament_id}/matches`);
      setMatches(data);
      toast.success("Score saved & settled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateMatchMeta = async (match_id, patch) => {
    try {
      await api.patch(`/matches/${match_id}`, patch);
      const { data } = await api.get(`/tournaments/${active.tournament_id}/matches`);
      setMatches(data);
      toast.success("Match updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createTournament = async () => {
    if (!newTour.name) return;
    try {
      const { data } = await api.post("/tournaments", newTour);
      await refresh();
      setActive(data.tournament_id);
      setNewTour({ name: "", sport: "football", format: "tournament", short_code: "" });
      toast.success("Tournament created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setLiveMatchdays = async (mds) => {
    try {
      await api.patch(`/admin/tournaments/${active.tournament_id}/live-matchdays`, { live_matchdays: mds });
      await refresh();
      setEditing((e) => ({ ...e, config: { ...e.config, live_matchdays: mds } }));
      toast.success("Matchday go-live updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!active || !editing) return null;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-center gap-3 mb-2">
        <Settings size={20} className="text-primary-accent" />
        <div className="label-tag">Admin Control Tower</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-6">{editing.name}</h1>

      <div className="flex gap-1 mb-6 surface-card p-1 inline-flex" data-testid="admin-tabs">
        {[
          { v: "overview", l: "Overview" },
          { v: "manual", l: "Manual" },
          { v: "users", l: "Users" },
          { v: "competitions", l: "Competitions" },
          { v: "scoring", l: "Scoring" },
          { v: "matches", l: "Matches" },
          { v: "predictions", l: "Predictions" },
          { v: "groups", l: "Groups" },
          { v: "feedback", l: "Feedback" },
          { v: "integrations", l: "Integrations" },
          { v: "tournaments", l: "Tournaments" },
          ...(currentUser?.is_owner ? [{ v: "danger", l: "Danger" }] : []),
        ].map((t) => (
          <button key={t.v} data-testid={`admin-tab-${t.v}`} onClick={() => setTab(t.v)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === t.v ? "bg-primary-accent text-white" : "text-zinc-400"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="surface-card p-5 accent-bar"><div className="label-tag">Users</div><div className="font-display text-4xl">{analytics.total_users || 0}</div></div>
          <div className="surface-card p-5"><div className="label-tag">Predictions</div><div className="font-display text-4xl">{analytics.total_predictions || 0}</div></div>
          <div className="surface-card p-5"><div className="label-tag">Settled</div><div className="font-display text-4xl">{analytics.settled_predictions || 0}</div></div>
          <div className="surface-card p-5"><div className="label-tag">Matches</div><div className="font-display text-4xl">{analytics.matches || 0}</div></div>
          <div className="surface-card p-5"><div className="label-tag">Completed</div><div className="font-display text-4xl">{analytics.completed_matches || 0}</div></div>
        </div>
      )}

      {tab === "manual" && <ManualTab />}

      {tab === "competitions" && (
        <div className="space-y-4 max-w-2xl">
          <MatchdayGoLive live={editing.config.live_matchdays || [1]} onChange={setLiveMatchdays} matchdays={allMatchdays} />
          <div className="surface-card p-6">
            <div className="label-tag mb-4">Toggle competitions enabled for this tournament. Disabled tabs hide for all users.</div>
          {COMP_KEYS.map((c) => {
            const on = editing.competitions[c.key];
            return (
              <div key={c.key} className="flex items-center justify-between py-3 border-b border-white/5">
                <span className="font-display text-lg">{c.label}</span>
                <button
                  data-testid={`toggle-${c.key}`}
                  onClick={() => toggleComp(c.key)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border ${on ? "border-primary-accent text-primary-accent" : "border-zinc-700 text-zinc-500"}`}
                >
                  {on ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
                  {on ? "Enabled" : "Disabled"}
                </button>
              </div>
            );
          })}
            <button data-testid="save-tournament" onClick={saveTournament} className="mt-6 bg-primary-accent text-white px-6 py-3 font-display tracking-widest uppercase flex items-center gap-2">
              <Save size={16}/> Save Changes
            </button>
          </div>
        </div>
      )}

      {tab === "scoring" && (
        <div className="surface-card p-6 max-w-2xl">
          <div className="label-tag mb-4 flex items-center gap-2"><Sliders size={14}/> Scoring Configuration</div>
          {[
            ["pts_correct_result", "Correct Result"],
            ["pts_correct_gd", "Goal Difference"],
            ["pts_exact_score", "Exact Score"],
            ["pts_underdog_bonus", "Underdog Bonus"],
            ["underdog_threshold", "Underdog Threshold (0-1)"],
            ["pts_group_first", "Group: Correct 1st"],
            ["pts_group_second", "Group: Correct 2nd"],
            ["pts_group_third", "Group: Correct 3rd"],
            ["pts_group_fourth", "Group: Correct 4th"],
            ["pts_best_third", "Group: Best 3rd advances"],
            ["pts_r32", "R32 Points"],
            ["pts_r16", "R16 Points"],
            ["pts_qf", "QF Points"],
            ["pts_sf", "SF Points"],
            ["pts_final", "Champion Points"],
            ["fantasy_budget", "Fantasy Budget"],
            ["max_per_nation", "Max Players/Nation"],
            ["captain_multiplier", "Captain Multiplier"],
            ["powerplay_multiplier", "Power Play Multiplier"],
            ["survivor_picks_per_matchday", "Survivor: Picks / Matchday"],
            ["survivor_strikes_allowed", "Survivor (Tournament): Strikes Before Out"],
            ["survivor_weekly_strikes_allowed", "Survivor (League): Strikes / Week Allowed"],
            ["survivor_survive_points", "Survivor (League): Survive Points"],
            ["cl_zone", "League Table: Top Zone Size (CL)"],
            ["relegation_zone", "League Table: Relegation Zone Size"],
            ["half_season_split", "League: Half-Season Split (last GW of 1st half)"],
            ["super_jinx_multiplier", "League: Super Jinx Multiplier"],
          ].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-white/5">
              <label className="text-sm text-zinc-300">{l}</label>
              <input
                data-testid={`cfg-${k}`}
                type="number" step="0.1" value={editing.config[k] ?? ""}
                onChange={(e) => { const v = parseFloat(e.target.value); updateCfg(k, Number.isNaN(v) ? "" : v); }}
                className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 w-24 text-right font-display text-lg focus:border-primary-accent focus:outline-none"
              />
            </div>
          ))}
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Group Scoring Method</label>
            <select
              data-testid="cfg-group_scoring_method"
              value={editing.config.group_scoring_method || "top_5"}
              onChange={(e) => updateCfg("group_scoring_method", e.target.value)}
              className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 focus:border-primary-accent focus:outline-none uppercase tracking-widest text-xs"
            >
              <option value="top_3">Top 3 Members</option>
              <option value="top_5">Top 5 Members</option>
              <option value="sum">Sum of All</option>
              <option value="average">Average</option>
            </select>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Super Jinx Chip <span className="text-[11px] text-zinc-500">(league only, once per half)</span></label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input data-testid="cfg-super_jinx_enabled" type="checkbox"
                checked={editing.config.super_jinx_enabled ?? true}
                onChange={(e) => updateCfg("super_jinx_enabled", e.target.checked)} />
              {(editing.config.super_jinx_enabled ?? true) ? "On" : "Off"}
            </label>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Club Groups <span className="text-[11px] text-zinc-500">(auto-create from favourite team)</span></label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input data-testid="cfg-club_groups_enabled" type="checkbox"
                checked={editing.config.club_groups_enabled ?? true}
                onChange={(e) => updateCfg("club_groups_enabled", e.target.checked)} />
              {(editing.config.club_groups_enabled ?? true) ? "On" : "Off"}
            </label>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Club Scoring Method</label>
            <select data-testid="cfg-club_scoring_method"
              value={editing.config.club_scoring_method || "average"}
              onChange={(e) => updateCfg("club_scoring_method", e.target.value)}
              className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 focus:border-primary-accent focus:outline-none uppercase tracking-widest text-xs">
              <option value="average">Average</option>
              <option value="sum">Sum of All</option>
              <option value="top_5">Top 5 Members</option>
            </select>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Affiliations Locked</label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input
                data-testid="cfg-affiliations_locked"
                type="checkbox"
                checked={!!editing.config.affiliations_locked}
                onChange={(e) => updateCfg("affiliations_locked", e.target.checked)}
              />
              {editing.config.affiliations_locked ? "Locked" : "Open"}
            </label>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <label className="text-sm text-zinc-300">Group Chat Recaps <span className="text-[11px] text-zinc-500">(FT result + best predictor, groups &gt;5 only)</span></label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input
                data-testid="cfg-group_recaps_enabled"
                type="checkbox"
                checked={editing.config.group_recaps_enabled ?? true}
                onChange={(e) => updateCfg("group_recaps_enabled", e.target.checked)}
              />
              {(editing.config.group_recaps_enabled ?? true) ? "On" : "Off"}
            </label>
          </div>
          <button data-testid="save-scoring" onClick={saveTournament} className="mt-6 bg-primary-accent text-white px-6 py-3 font-display tracking-widest uppercase flex items-center gap-2">
            <Save size={16}/> Save Scoring
          </button>
        </div>
      )}

      {tab === "matches" && (
        <div className="space-y-4">
          <div className="max-w-2xl"><MatchdayGoLive live={editing.config.live_matchdays || [1]} onChange={setLiveMatchdays} matchdays={allMatchdays} /></div>
          {editing.format === "league" && <EloRatings tournamentId={editing.tournament_id} teams={teams} reloadTeams={reloadTeams} />}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-tag">Show</span>
            <select
              data-testid="match-gw-select"
              value={matchView}
              onChange={(e) => setMatchView(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-xs uppercase tracking-widest focus:border-primary-accent focus:outline-none"
            >
              {matchBuckets.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
            <span className="text-xs text-zinc-500">{visibleMatches.length} matches</span>
          </div>
          <div className="surface-card overflow-x-auto">
            <table className="w-full min-w-[920px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag">Match</th>
                <th className="text-left px-4 py-3 label-tag">Kickoff</th>
                <th className="text-left px-4 py-3 label-tag">Stage</th>
                <th className="text-center px-4 py-3 label-tag w-24">Matchday</th>
                <th className="text-center px-4 py-3 label-tag">Status</th>
                <th className="text-right px-4 py-3 label-tag">Score</th>
                <th className="text-right px-4 py-3 label-tag">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleMatches.map((m) => (
                <MatchRow key={m.match_id} m={m} teams={teams} setScore={setMatchScore} updateMeta={updateMatchMeta} />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "groups" && <GroupsTab tournamentId={active.tournament_id} />}
      {tab === "predictions" && <PredictionsTab tournament={active} />}

      {tab === "feedback" && <FeedbackTab />}

      {tab === "users" && <UsersTab currentUserId={currentUser?.user_id} tournamentId={active.tournament_id} />}

      {tab === "integrations" && <IntegrationsTab tournament={active} refresh={refresh} />}

      {tab === "tournaments" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-display text-2xl uppercase mb-3">Existing</h3>
            <div className="space-y-2">
              {tournaments.map((t) => (
                <div key={t.tournament_id} className="surface-card p-3 flex items-center justify-between" data-testid={`tour-${t.short_code}`}>
                  <div>
                    <div className="font-display text-lg flex items-center gap-2">{t.name}
                      {t.status === "archived" && <span className="text-[10px] uppercase tracking-widest border border-yellow-500/50 text-yellow-400 px-1.5 py-0.5" data-testid={`tour-ended-badge-${t.short_code}`}>Ended</span>}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-widest">{t.sport} · {t.format || "tournament"} · {t.status}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setActive(t.tournament_id)} className="text-xs uppercase tracking-widest text-primary-accent" data-testid={`switch-tour-${t.short_code}`}>Switch</button>
                    <button onClick={() => toggleArchive(t)} title={t.status === "archived" ? "Reactivate" : "End / archive tournament"} data-testid={`archive-tour-${t.short_code}`}
                      className="text-xs uppercase tracking-widest text-zinc-400 hover:text-yellow-400 transition-colors">{t.status === "archived" ? "Reactivate" : "End"}</button>
                    <button onClick={() => setDeleteTarget(t)} title="Delete tournament" data-testid={`delete-tour-${t.short_code}`}
                      className="text-zinc-500 hover:text-primary-accent transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {deleteTarget && (
            <div className="fixed inset-0 z-[220] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-tour-modal">
              <div className="surface-card max-w-md w-full p-6 border border-primary-accent/50">
                <div className="flex items-center gap-2 mb-3">
                  <Trash2 size={20} className="text-primary-accent" />
                  <h3 className="font-display text-xl uppercase text-primary-accent">Delete Tournament</h3>
                </div>
                <p className="text-sm text-zinc-300 mb-3">
                  Permanently delete <span className="text-white font-display">{deleteTarget.name}</span> and all its teams, matches, groups and settings? This cannot be undone. (Only allowed before any predictions are made.)
                </p>
                {deleteTarget.status === "active" && (
                  <div className="border border-primary-accent/40 bg-primary-accent/10 px-3 py-2 mb-3">
                    <p className="text-xs text-primary-accent uppercase tracking-widest mb-2">⚠ This tournament is ACTIVE — type DELETE to confirm</p>
                    <input
                      data-testid="delete-tour-confirm-input"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                      className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 uppercase tracking-widest text-sm focus:border-primary-accent focus:outline-none"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    data-testid="delete-tour-confirm-btn"
                    onClick={confirmDelete}
                    disabled={deleteTarget.status === "active" && deleteConfirmText.trim().toUpperCase() !== "DELETE"}
                    className="flex-1 bg-primary-accent text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  ><Trash2 size={12} className="inline mr-1" /> Delete Forever</button>
                  <button
                    data-testid="delete-tour-cancel-btn"
                    onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}
                    className="px-5 py-2.5 text-xs uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:border-white/40"
                  >Cancel</button>
                </div>
              </div>
            </div>
          )}
          <div>
            <h3 className="font-display text-2xl uppercase mb-3">New Tournament</h3>
            <div className="surface-card p-4 space-y-3">
              <input data-testid="new-tour-name" placeholder="Name (e.g. Premier League 2026)" value={newTour.name} onChange={(e) => setNewTour({ ...newTour, name: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none" />
              <input data-testid="new-tour-code" placeholder="Short code (e.g. EPL26)" value={newTour.short_code} onChange={(e) => setNewTour({ ...newTour, short_code: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none" />
              <div>
                <label className="label-tag mb-1 block">Format</label>
                <select data-testid="new-tour-format" value={newTour.format} onChange={(e) => setNewTour({ ...newTour, format: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none uppercase tracking-widest text-xs">
                  <option value="tournament">Tournament (groups + knockouts)</option>
                  <option value="league">League (single round-robin table)</option>
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {newTour.format === "league"
                    ? "One league table, N gameweeks, ongoing survivor (weekly points). E.g. Premier League."
                    : "Group stage → knockout bracket, best-3rds. E.g. FIFA World Cup."}
                </p>
              </div>
              <select data-testid="new-tour-sport" value={newTour.sport} onChange={(e) => setNewTour({ ...newTour, sport: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none">
                <option value="football">Football</option>
                <option value="cricket">Cricket</option>
                <option value="basketball">Basketball</option>
                <option value="other">Other</option>
              </select>
              <button data-testid="create-tour" onClick={createTournament} className="bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest">Create Tournament</button>
            </div>
          </div>
        </div>
      )}
      {tab === "danger" && currentUser?.is_owner && <DangerTab tournament={active} refresh={refresh} />}
    </div>
  );
}

function PredictionsTab({ tournament }) {
  const [view, setView] = useState("predictions"); // predictions | survivor
  return (
    <div className="space-y-3">
      <div className="flex gap-1 surface-card p-1 w-fit" data-testid="admin-picks-subtabs">
        {[
          { v: "predictions", l: "Match Predictions" },
          { v: "survivor", l: "Survivor Picks" },
        ].map((t) => (
          <button
            key={t.v}
            data-testid={`admin-picks-subtab-${t.v}`}
            onClick={() => setView(t.v)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest ${view === t.v ? "bg-primary-accent text-white" : "text-zinc-400 hover:text-white"}`}
          >
            {t.l}
          </button>
        ))}
      </div>
      {view === "predictions" ? <MatchPredictionsView tournament={tournament} /> : <SurvivorPicksView tournament={tournament} />}
    </div>
  );
}

function MatchPredictionsView({ tournament }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/locked-predictions/${tournament.tournament_id}`)
      .then((r) => { setMatches(r.data || []); setOpenId(r.data?.[0]?.match_id || null); })
      .catch(() => setMatches([]))
      .finally(() => setLoading(false));
  }, [tournament.tournament_id]);

  if (loading) return <div className="surface-card p-6 text-zinc-500">Loading locked predictions…</div>;
  if (!matches.length) return <div className="surface-card p-6 text-zinc-500" data-testid="admin-predictions-empty">No matches have locked yet.</div>;

  return (
    <div className="space-y-3" data-testid="admin-predictions-tab">
      <p className="text-xs text-zinc-500 uppercase tracking-widest">Locked picks for every player · ★ = Captain · 3x = Power Play</p>
      {matches.map((m) => {
        const open = openId === m.match_id;
        return (
          <div key={m.match_id} className="surface-card overflow-hidden" data-testid={`admin-pred-match-${m.match_id}`}>
            <button
              onClick={() => setOpenId(open ? null : m.match_id)}
              data-testid={`admin-pred-toggle-${m.match_id}`}
              className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/5"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {m.stage === "group" && m.matchday != null ? `Matchday ${m.matchday}` : (m.stage || "").toUpperCase()}
                  {m.kickoff && <span className="text-zinc-600"> · {new Date(m.kickoff).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
                <div className="font-display text-lg uppercase truncate">
                  {m.home} <span className="text-zinc-500">vs</span> {m.away}
                  {m.settled && m.home_score != null && <span className="text-primary-accent ml-2">{m.home_score}–{m.away_score} FT</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs uppercase tracking-widest text-zinc-400">{m.prediction_count} pick{m.prediction_count === 1 ? "" : "s"}</span>
                <span className="text-zinc-500">{open ? "−" : "+"}</span>
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto border-t border-white/5">
                {m.predictions.length === 0 ? (
                  <div className="p-4 text-sm text-zinc-500">No predictions for this match.</div>
                ) : (
                  <table className="w-full min-w-[480px] text-sm">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left px-4 py-2 label-tag">Player</th>
                        <th className="text-center px-4 py-2 label-tag">Pick</th>
                        <th className="text-center px-4 py-2 label-tag">Chips</th>
                        <th className="text-right px-4 py-2 label-tag">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.predictions.map((p) => (
                        <tr key={p.user_id} className="border-b border-white/5" data-testid={`admin-pred-row-${m.match_id}-${p.user_id}`}>
                          <td className="px-4 py-2">{p.name}</td>
                          <td className="px-4 py-2 text-center font-display tabular-nums">{p.home_pred}–{p.away_pred}</td>
                          <td className="px-4 py-2 text-center">
                            {p.is_captain && <span className="text-yellow-400 mr-1" title="Captain">★</span>}
                            {p.power_play && <span className="text-primary-accent" title="Power Play">3x</span>}
                            {!p.is_captain && !p.power_play && <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="px-4 py-2 text-right font-display">
                            {p.points == null ? <span className="text-zinc-600 text-xs uppercase">live</span> : <span className={p.points > 0 ? "text-primary-accent" : "text-zinc-500"}>{p.points}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SurvivorPicksView({ tournament }) {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/locked-survivor-picks/${tournament.tournament_id}`)
      .then((r) => { setBuckets(r.data || []); setOpenKey((k) => k || r.data?.[0]?.key || null); })
      .catch(() => setBuckets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tournament.tournament_id]);

  const resultStyle = (r) =>
    r === "survived" ? "text-green-400 border-green-500/40 bg-green-500/10"
      : r === "strike" ? "text-primary-accent border-primary-accent/40 bg-primary-accent/10"
        : "text-zinc-400 border-zinc-700 bg-white/5";

  return (
    <div className="space-y-3" data-testid="admin-survivor-tab">
      <AddSurvivorPickForm tournament={tournament} onAdded={load} />
      <p className="text-xs text-zinc-500 uppercase tracking-widest">Locked survivor picks per player · <span className="text-green-400">survived</span> · <span className="text-primary-accent">strike</span></p>
      {loading ? (
        <div className="surface-card p-6 text-zinc-500">Loading locked survivor picks…</div>
      ) : !buckets.length ? (
        <div className="surface-card p-6 text-zinc-500" data-testid="admin-survivor-empty">No survivor matchdays have locked yet.</div>
      ) : buckets.map((b) => {
        const open = openKey === b.key;
        return (
          <div key={b.key} className="surface-card overflow-hidden" data-testid={`admin-surv-bucket-${b.key}`}>
            <button
              onClick={() => setOpenKey(open ? null : b.key)}
              data-testid={`admin-surv-toggle-${b.key}`}
              className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/5"
            >
              <div className="font-display text-lg uppercase truncate">{b.label}</div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs uppercase tracking-widest text-zinc-400">{b.player_count} player{b.player_count === 1 ? "" : "s"}</span>
                <span className="text-zinc-500">{open ? "−" : "+"}</span>
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto border-t border-white/5">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-4 py-2 label-tag">Player</th>
                      <th className="text-left px-4 py-2 label-tag">Picks (team · vs opponent)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.users.map((u) => (
                      <tr key={u.user_id} className="border-b border-white/5 align-top" data-testid={`admin-surv-row-${b.key}-${u.user_id}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap">{u.name}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1.5">
                            {u.picks.map((p, i) => (
                              <span key={i} title={`${p.team_name} vs ${p.opponent}${p.score ? ` (${p.score})` : ""}`}
                                className={`inline-flex items-center gap-1 px-2 py-1 border text-xs uppercase tracking-wide ${resultStyle(p.result)}`}>
                                <span className="font-display">{p.team_code || p.team_name}</span>
                                <span className="text-zinc-500 normal-case">vs {p.opponent_code || p.opponent}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddSurvivorPickForm({ tournament, onAdded }) {
  const [openForm, setOpenForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [userId, setUserId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!openForm) return;
    const tid = tournament.tournament_id;
    Promise.all([
      api.get(`/admin/users`),
      api.get(`/tournaments/${tid}/matches`),
      api.get(`/tournaments/${tid}/teams`),
    ]).then(([uR, mR, tR]) => {
      const ulist = Array.isArray(uR.data) ? uR.data : (uR.data?.users || []);
      setUsers(ulist);
      setMatches((mR.data || []).filter((m) => m.stage === "group" && m.matchday != null)
        .sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff)));
      setTeams(Object.fromEntries((tR.data || []).map((t) => [t.team_id, t])));
    }).catch(() => {});
  }, [openForm, tournament.tournament_id]);

  const selMatch = matches.find((m) => m.match_id === matchId);

  const submit = async () => {
    if (!userId || !matchId || !teamId) { toast.error("Pick a player, match and team"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tournaments/${tournament.tournament_id}/survivor/pick`,
        { user_id: userId, match_id: matchId, team_id: teamId });
      toast.success(`Pick added (${data.result})`);
      setMatchId(""); setTeamId("");
      onAdded && onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add pick");
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-4" data-testid="admin-survivor-addform">
      <button onClick={() => setOpenForm((o) => !o)} data-testid="admin-survivor-addform-toggle"
        className="flex items-center gap-2 text-sm font-display uppercase tracking-widest text-primary-accent">
        <Plus size={16} /> Add a survivor pick for a player
      </button>
      {openForm && (
        <div className="mt-4 grid md:grid-cols-4 gap-2 items-end">
          <label className="block">
            <span className="label-tag block mb-1">Player</span>
            <select data-testid="addsurv-user" value={userId} onChange={(e) => setUserId(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-sm px-2 py-2 focus:border-primary-accent outline-none">
              <option value="">Select player…</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label-tag block mb-1">Match</span>
            <select data-testid="addsurv-match" value={matchId}
              onChange={(e) => { setMatchId(e.target.value); setTeamId(""); }}
              className="w-full bg-zinc-900 border border-zinc-800 text-sm px-2 py-2 focus:border-primary-accent outline-none">
              <option value="">Select match…</option>
              {matches.map((m) => (
                <option key={m.match_id} value={m.match_id}>
                  MD{m.matchday} · {teams[m.home_team_id]?.code || teams[m.home_team_id]?.name || "?"} v {teams[m.away_team_id]?.code || teams[m.away_team_id]?.name || "?"}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label-tag block mb-1">Team</span>
            <select data-testid="addsurv-team" value={teamId} onChange={(e) => setTeamId(e.target.value)} disabled={!selMatch}
              className="w-full bg-zinc-900 border border-zinc-800 text-sm px-2 py-2 focus:border-primary-accent outline-none disabled:opacity-40">
              <option value="">Select team…</option>
              {selMatch && [selMatch.home_team_id, selMatch.away_team_id].map((tid) => (
                <option key={tid} value={tid}>{teams[tid]?.name || teams[tid]?.code || tid}</option>
              ))}
            </select>
          </label>
          <button onClick={submit} disabled={busy} data-testid="addsurv-submit"
            className="bg-primary-accent text-white text-sm uppercase tracking-widest py-2 hover:opacity-90 disabled:opacity-50">
            {busy ? "Adding…" : "Add Pick"}
          </button>
        </div>
      )}
    </div>
  );
}

function DangerTab({ tournament, refresh }) {
  const [stage, setStage] = useState("idle"); // idle | confirm
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const recalc = async () => {
    if (!window.confirm(`Re-run the current scoring engine over ALL settled predictions for "${tournament.name}"? This only recomputes points (non-destructive).`)) return;
    setRecalcBusy(true);
    try {
      const { data } = await api.post(`/admin/recalculate-scores/${tournament.tournament_id}`, { confirm: "RECALC" });
      setRecalcResult(data);
      await refresh();
      toast.success(`Recalculated ${data.predictions_recalculated} predictions across ${data.completed_matches} matches`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalculation failed");
    } finally { setRecalcBusy(false); }
  };

  const reset = async () => {
    if (token !== "RESET") return;
    // Second confirmation gate
    if (!window.confirm(`FINAL CHECK — permanently wipe ALL player & score data for "${tournament.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reset-tournament/${tournament.tournament_id}`, { confirm: "RESET" });
      setResult(data);
      setStage("idle");
      setToken("");
      await refresh();
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reset complete — ${total} records cleared, ${data.matches_reset} matches reset`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="danger-tab">
      {/* RECALCULATE SCORES — non-destructive */}
      <div className="surface-card p-6 border border-zinc-700">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw size={18} className="text-primary-accent" />
          <h3 className="font-display text-2xl uppercase">Recalculate Scores</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Re-runs the current <span className="text-white">non-additive</span> scoring engine
          (Exact &gt; GD &gt; Result, + underdog bonus) over every settled prediction for{" "}
          <span className="text-white">{tournament.name}</span>. Use this after changing scoring rules to align historical points. Non-destructive — only points are recomputed.
        </p>
        <button
          data-testid="recalc-scores-btn"
          onClick={recalc}
          disabled={recalcBusy}
          className="border border-primary-accent text-primary-accent px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-primary-accent hover:text-white transition-colors disabled:opacity-40"
        >
          {recalcBusy ? "Recalculating…" : "Recalculate All Scores"}
        </button>
        {recalcResult && (
          <div className="mt-4 border border-zinc-800 p-3 text-xs text-zinc-400" data-testid="recalc-result">
            <span className="text-white tabular-nums">{recalcResult.predictions_recalculated}</span> predictions recalculated across{" "}
            <span className="text-white tabular-nums">{recalcResult.completed_matches}</span> completed matches.
          </div>
        )}
      </div>

      <div className="surface-card p-6 border border-primary-accent/50 bg-primary-accent/[0.04]">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={20} className="text-primary-accent" />
          <h3 className="font-display text-2xl uppercase text-primary-accent">Danger Zone</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-1">
          Wipe <span className="text-white">all player &amp; score data</span> for <span className="text-white">{tournament.name}</span> to get a clean slate for testing.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4 text-xs">
          <div className="border border-zinc-800 p-3">
            <div className="label-tag text-primary-accent mb-1">Will be wiped</div>
            <ul className="text-zinc-400 space-y-0.5 list-disc list-inside">
              <li>Predictions, Survivor, Bracket, Fantasy, Bonus answers</li>
              <li>Match results (scores cleared, set to Scheduled)</li>
              <li>Points, leaderboard history, rivalries</li>
              <li>Chat posts &amp; votes, notifications</li>
            </ul>
          </div>
          <div className="border border-zinc-800 p-3">
            <div className="label-tag text-zinc-300 mb-1">Will be kept</div>
            <ul className="text-zinc-400 space-y-0.5 list-disc list-inside">
              <li>User accounts &amp; logins</li>
              <li>Tournament config, teams &amp; FIFA rankings</li>
              <li>Fixtures (kickoffs &amp; matchups)</li>
              <li>Groups &amp; player affiliations</li>
            </ul>
          </div>
        </div>

        {stage === "idle" ? (
          <button
            data-testid="reset-data-btn"
            onClick={() => { setStage("confirm"); setResult(null); }}
            className="border border-primary-accent text-primary-accent px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-primary-accent hover:text-white transition-colors"
          >
            Reset All Player &amp; Score Data
          </button>
        ) : (
          <div className="border border-primary-accent/60 p-4 bg-black/30" data-testid="reset-confirm-panel">
            <p className="text-sm text-zinc-300 mb-2">
              Type <span className="font-display text-primary-accent">RESET</span> to confirm. This is irreversible.
            </p>
            <input
              data-testid="reset-confirm-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="RESET"
              className="w-40 bg-zinc-950 border border-zinc-800 px-3 py-2 font-display tracking-widest uppercase focus:border-primary-accent focus:outline-none mb-3 block"
            />
            <div className="flex items-center gap-3">
              <button
                data-testid="reset-confirm-btn"
                onClick={reset}
                disabled={token !== "RESET" || busy}
                className="bg-primary-accent text-white px-5 py-2.5 text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--primary-hover)]"
              >
                {busy ? "Resetting…" : "Permanently Reset"}
              </button>
              <button
                data-testid="reset-cancel-btn"
                onClick={() => { setStage("idle"); setToken(""); }}
                className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-4 border border-zinc-800 p-3" data-testid="reset-result">
            <div className="label-tag text-primary-accent mb-2">Reset complete · {result.matches_reset} matches reset</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-xs text-zinc-400">
              {Object.entries(result.deleted || {}).map(([k, v]) => (
                <div key={k}><span className="text-white tabular-nums">{v}</span> {k.replace(/_/g, " ")}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchRow({ m, teams, setScore, updateMeta }) {
  const [h, setH] = useState(m.home_score ?? 0);
  const [a, setA] = useState(m.away_score ?? 0);
  const [md, setMd] = useState(m.matchday ?? "");
  const [pen, setPen] = useState(m.penalty_winner ?? null);
  const ht = teams[m.home_team_id]; const at = teams[m.away_team_id];
  const status = m.status || "scheduled";
  const isKO = m.stage && m.stage !== "group";
  const needsPen = isKO && Number(h) === Number(a);

  const saveMd = () => {
    const n = md === "" ? null : parseInt(md, 10);
    if (n === (m.matchday ?? null)) return;
    updateMeta(m.match_id, { matchday: n });
  };

  const goLive = () => updateMeta(m.match_id, { status: "live" });
  const setScheduled = () => updateMeta(m.match_id, { status: "scheduled" });

  return (
    <tr className="border-b border-white/5" data-testid={`admin-match-${m.match_id}`}>
      <td className="px-4 py-2.5 text-sm">
        <span className="mr-2">{ht?.flag}</span>{ht?.name} vs {at?.name}<span className="ml-2">{at?.flag}</span>
      </td>
      <td className="px-4 py-2.5 text-xs text-zinc-400">{new Date(m.kickoff).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</td>
      <td className="px-4 py-2.5 text-xs uppercase tracking-widest">{m.group ? `Group ${m.group}` : m.stage}</td>
      <td className="px-4 py-2.5">
        <input
          data-testid={`admin-md-${m.match_id}`}
          type="number" min={1} placeholder="—"
          value={md} onChange={(e) => setMd(e.target.value)} onBlur={saveMd}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="w-16 mx-auto bg-zinc-950 border border-zinc-800 text-center font-display text-lg focus:border-primary-accent focus:outline-none block"
        />
      </td>
      <td className="px-4 py-2.5 text-center">
        {status === "live" ? (
          <span data-testid={`admin-status-${m.match_id}`} className="inline-flex items-center gap-1.5 px-2 py-1 text-xs uppercase tracking-widest bg-primary-accent text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Live
          </span>
        ) : status === "completed" ? (
          <span data-testid={`admin-status-${m.match_id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-widest border border-zinc-700 text-zinc-400">FT</span>
        ) : (
          <span data-testid={`admin-status-${m.match_id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-widest border border-zinc-800 text-zinc-500">Scheduled</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1 justify-end">
          <input data-testid={`admin-h-${m.match_id}`} type="number" min={0} value={h} onChange={(e) => setH(parseInt(e.target.value || "0", 10))}
            className="w-14 bg-zinc-950 border border-zinc-800 text-center font-display text-lg focus:border-primary-accent focus:outline-none"/>
          <span>-</span>
          <input data-testid={`admin-a-${m.match_id}`} type="number" min={0} value={a} onChange={(e) => setA(parseInt(e.target.value || "0", 10))}
            className="w-14 bg-zinc-950 border border-zinc-800 text-center font-display text-lg focus:border-primary-accent focus:outline-none"/>
        </div>
        {needsPen && (
          <div className="mt-1.5 flex items-center gap-1 justify-end" data-testid={`admin-pen-${m.match_id}`}>
            <span className="text-[9px] uppercase tracking-widest text-purple-300 mr-1">Pens:</span>
            <button data-testid={`admin-pen-home-${m.match_id}`} onClick={() => setPen(pen === m.home_team_id ? null : m.home_team_id)}
              className={`px-1.5 py-0.5 text-[10px] uppercase border ${pen === m.home_team_id ? "bg-purple-500 text-white border-purple-500" : "border-zinc-700 text-zinc-400 hover:border-purple-400"}`}>{ht?.code || ht?.name}</button>
            <button data-testid={`admin-pen-away-${m.match_id}`} onClick={() => setPen(pen === m.away_team_id ? null : m.away_team_id)}
              className={`px-1.5 py-0.5 text-[10px] uppercase border ${pen === m.away_team_id ? "bg-purple-500 text-white border-purple-500" : "border-zinc-700 text-zinc-400 hover:border-purple-400"}`}>{at?.code || at?.name}</button>
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="inline-flex items-center gap-1.5">
          {status !== "completed" && status !== "live" && (
            <button
              data-testid={`admin-go-live-${m.match_id}`}
              onClick={goLive}
              className="border border-primary-accent text-primary-accent hover:bg-primary-accent hover:text-white px-2 py-1 text-xs uppercase tracking-widest"
              title="Force this match to LIVE"
            >Go Live</button>
          )}
          {status === "live" && (
            <button
              data-testid={`admin-end-live-${m.match_id}`}
              onClick={setScheduled}
              className="border border-zinc-700 text-zinc-300 hover:border-white px-2 py-1 text-xs uppercase tracking-widest"
              title="Revert to scheduled"
            >End Live</button>
          )}
          <button data-testid={`admin-settle-${m.match_id}`} onClick={() => setScore(m.match_id, h, a, needsPen ? pen : null)} className="bg-primary-accent text-white px-3 py-1 text-xs uppercase tracking-widest">Settle</button>
        </div>
      </td>
    </tr>
  );
}


function RequestRow({ req, onApprove, onReject }) {
  const [name, setName] = useState(req.name);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 border border-white/5 px-3 py-2.5" data-testid={`group-request-${req.request_id}`}>
      <div className="flex-1 min-w-0">
        <input
          data-testid={`request-name-input-${req.request_id}`}
          value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-sm focus:border-primary-accent focus:outline-none"
        />
        <div className="text-xs text-zinc-500 mt-1 truncate">
          {req.description || "—"} · by <span className="text-zinc-300">{req.requester_name}</span>
          <span className={`ml-2 uppercase tracking-widest ${req.visibility === "invite_only" ? "text-zinc-400" : "text-green-400"}`}>
            {req.visibility === "invite_only" ? "Invite-only" : "Public"}
          </span>
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button data-testid={`approve-request-${req.request_id}`} onClick={() => onApprove(req, name.trim() || req.name)}
          className="bg-primary-accent text-white px-3 py-1.5 text-xs uppercase tracking-widest">Approve</button>
        <button data-testid={`reject-request-${req.request_id}`} onClick={() => onReject(req)}
          className="border border-zinc-700 text-zinc-400 hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest">Reject</button>
      </div>
    </div>
  );
}

function GroupsTab({ tournamentId }) {
  const [groups, setGroups] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#FF3B30", logo_url: "", visibility: "public" });
  const [editing, setEditing] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/tournaments/${tournamentId}/groups`, { params: { include_archived: showArchived } });
    setGroups(data);
  };

  const loadRequests = async () => {
    const { data } = await api.get(`/tournaments/${tournamentId}/group-requests`, { params: { status: "pending" } });
    setRequests(data);
  };

  useEffect(() => { load(); }, [tournamentId, showArchived]);
  useEffect(() => { loadRequests(); }, [tournamentId]);

  const approveRequest = async (req, name) => {
    try {
      await api.post(`/group-requests/${req.request_id}/approve`, { name });
      toast.success(`Group "${name}" created — ${req.requester_name} is its admin`);
      loadRequests(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
  };

  const rejectRequest = async (req) => {
    try {
      await api.post(`/group-requests/${req.request_id}/reject`);
      toast.success("Request rejected");
      loadRequests();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reject"); }
  };

  const create = async () => {
    if (!form.name) return;
    try {
      await api.post(`/tournaments/${tournamentId}/groups`, form);
      setForm({ name: "", description: "", color: "#FF3B30", logo_url: "", visibility: "public" });
      toast.success("Group created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (group_id, patch) => {
    try {
      await api.patch(`/groups/${group_id}`, patch);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (group_id) => {
    try {
      const { data } = await api.delete(`/groups/${group_id}`);
      toast.success(data.archived ? "Archived (has members)" : "Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div data-testid="group-requests-section">
        <h3 className="font-display text-2xl uppercase mb-3">Pending Group Requests {requests.length > 0 && <span className="text-primary-accent">({requests.length})</span>}</h3>
        <div className="surface-card p-4 space-y-2">
          {requests.length === 0 ? (
            <div className="text-sm text-zinc-500" data-testid="no-requests">No pending requests.</div>
          ) : (
            requests.map((r) => (
              <RequestRow key={r.request_id} req={r} onApprove={approveRequest} onReject={rejectRequest} />
            ))
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl uppercase">Groups</h3>
          <label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2" data-testid="show-archived">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Include archived
          </label>
        </div>
        <div className="surface-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-3 py-2 label-tag">Name</th>
                <th className="text-left px-3 py-2 label-tag">Description</th>
                <th className="text-left px-3 py-2 label-tag">Status</th>
                <th className="text-right px-3 py-2 label-tag">Action</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.group_id} className="border-b border-white/5" data-testid={`admin-group-${g.group_id}`}>
                  {editing === g.group_id ? (
                    <GroupEditRow g={g} onSave={(patch) => update(g.group_id, patch)} onCancel={() => setEditing(null)} />
                  ) : (
                    <>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="w-1 h-6 inline-block" style={{ background: g.color }}/>
                          <span className="font-display text-base">{g.name}</span>
                          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${(g.visibility === "invite_only") ? "border-zinc-700 text-zinc-400" : "border-green-500/40 text-green-400"}`} data-testid={`group-visibility-badge-${g.group_id}`}>
                            {g.visibility === "invite_only" ? "Invite-only" : "Public"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-zinc-400">{g.description || "—"}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs uppercase tracking-widest ${g.status === "active" ? "text-primary-accent" : "text-zinc-500"}`}>{g.status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right space-x-2">
                        <Link data-testid={`manage-group-link-${g.group_id}`} to={`/groups/${g.group_id}/manage`} className="text-xs uppercase tracking-widest text-primary-accent hover:text-white">Manage</Link>
                        <button data-testid={`edit-group-${g.group_id}`} onClick={() => setEditing(g.group_id)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Edit</button>
                        {g.status === "active" && <button data-testid={`archive-group-${g.group_id}`} onClick={() => update(g.group_id, { status: "archived" })} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Archive</button>}
                        {g.status === "archived" && <button data-testid={`activate-group-${g.group_id}`} onClick={() => update(g.group_id, { status: "active" })} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Activate</button>}
                        <button data-testid={`delete-group-${g.group_id}`} onClick={() => remove(g.group_id)} className="text-xs uppercase tracking-widest text-primary-accent hover:text-[var(--primary-hover)]">Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {groups.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500">No groups yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-display text-2xl uppercase mb-3">New Group</h3>
        <div className="surface-card p-4 space-y-3">
          <input data-testid="group-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"/>
          <input data-testid="group-desc-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"/>
          <input data-testid="group-logo-input" placeholder="Logo URL (optional)" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"/>
          <div>
            <span className="label-tag block mb-1">Visibility</span>
            <select data-testid="group-visibility-input" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none text-sm">
              <option value="public">Public — anyone can join</option>
              <option value="invite_only">Invite-only — via link</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="label-tag flex-1">Color</span>
            <input data-testid="group-color-input" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="w-12 h-8 bg-zinc-950 border border-zinc-800"/>
            <span className="font-display text-sm">{form.color}</span>
          </div>
          <button data-testid="create-group-btn" onClick={create} className="w-full bg-primary-accent text-white py-2 text-xs uppercase tracking-widest">Create Group</button>
        </div>
      </div>
      </div>
    </div>
  );
}

function GroupEditRow({ g, onSave, onCancel }) {
  const [v, setV] = useState({ name: g.name, description: g.description, color: g.color, logo_url: g.logo_url || "" });
  return (
    <>
      <td className="px-3 py-2"><input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })}
        className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-sm focus:border-primary-accent focus:outline-none"/></td>
      <td className="px-3 py-2"><input value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })}
        className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1 text-sm focus:border-primary-accent focus:outline-none"/></td>
      <td className="px-3 py-2"><input type="color" value={v.color} onChange={(e) => setV({ ...v, color: e.target.value })}
        className="w-10 h-7 bg-zinc-950"/></td>
      <td className="px-3 py-2 text-right space-x-2">
        <button data-testid={`save-edit-${g.group_id}`} onClick={() => onSave(v)} className="bg-primary-accent text-white px-2 py-1 text-xs uppercase tracking-widest">Save</button>
        <button onClick={onCancel} className="text-xs uppercase tracking-widest text-zinc-400">Cancel</button>
      </td>
    </>
  );
}

function UsersTab({ currentUserId, tournamentId }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const params = {};
    if (search) params.q = search;
    if (roleFilter) params.role = roleFilter;
    if (tournamentId) params.tournament_id = tournamentId;
    const { data } = await api.get("/admin/users", { params });
    setUsers(data);
  };

  useEffect(() => { load();   }, [roleFilter, tournamentId]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const setRole = async (user_id, role) => {
    setBusy(user_id);
    try {
      await api.patch(`/admin/users/${user_id}`, { role });
      toast.success(`Role updated to ${role}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const removeUser = async (user_id, name) => {
    if (!window.confirm(`Delete user "${name}"? This will revoke their sessions and remove their profile.`)) return;
    setBusy(user_id);
    try {
      await api.delete(`/admin/users/${user_id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h3 className="font-display text-2xl uppercase flex items-center gap-2">
          <UsersIcon size={20} className="text-primary-accent"/> User Management
        </h3>
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <input
            data-testid="user-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email..."
            className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-sm focus:border-primary-accent focus:outline-none w-64"
          />
          <button data-testid="user-search-btn" type="submit" className="bg-primary-accent text-white px-3 py-1.5 text-xs uppercase tracking-widest">Search</button>
          <select
            data-testid="user-role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-xs uppercase tracking-widest focus:border-primary-accent focus:outline-none"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="participant">Participant</option>
          </select>
        </form>
      </div>

      <div className="text-xs text-zinc-500 uppercase tracking-widest mb-3" data-testid="users-count">{users.length} user{users.length !== 1 ? "s" : ""} signed up for this tournament</div>

      <div className="surface-card overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 label-tag">User</th>
              <th className="text-left px-4 py-3 label-tag">Provider</th>
              <th className="text-left px-4 py-3 label-tag">Role</th>
              <th className="text-right px-4 py-3 label-tag">Predictions</th>
              <th className="text-right px-4 py-3 label-tag">Points</th>
              <th className="text-right px-4 py-3 label-tag">Joined</th>
              <th className="text-right px-4 py-3 label-tag">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-500 py-8">No users match this filter.</td></tr>
            )}
            {users.map((u) => {
              const isSelf = u.user_id === currentUserId;
              const isAdmin = u.role === "admin";
              return (
                <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-user-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-8 h-8 object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center text-xs uppercase tracking-widest text-zinc-500">
                          {u.name?.[0] || "?"}
                        </div>
                      )}
                      <div>
                        <div className="font-display text-base tracking-wider">
                          {u.name}
                          {isSelf && <span className="ml-2 text-xs uppercase tracking-widest text-primary-accent">(you)</span>}
                        </div>
                        <div className="text-xs text-zinc-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest text-zinc-400">{u.auth_provider}</td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`role-badge-${u.user_id}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-widest border ${
                        isAdmin ? "border-primary-accent text-primary-accent" : "border-zinc-700 text-zinc-400"
                      }`}
                    >
                      {isAdmin && <Shield size={10}/>}{u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{u.predictions}</td>
                  <td className="px-4 py-3 text-right font-display text-xl text-primary-accent">{u.points}</td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isAdmin ? (
                        <button
                          data-testid={`demote-${u.user_id}`}
                          disabled={isSelf || busy === u.user_id}
                          onClick={() => setRole(u.user_id, "participant")}
                          className="text-xs uppercase tracking-widest border border-zinc-700 px-2 py-1 hover:border-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >Demote</button>
                      ) : (
                        <button
                          data-testid={`promote-${u.user_id}`}
                          disabled={busy === u.user_id}
                          onClick={() => setRole(u.user_id, "admin")}
                          className="text-xs uppercase tracking-widest border border-primary-accent text-primary-accent px-2 py-1 hover:bg-primary-accent hover:text-white disabled:opacity-40"
                        >Promote</button>
                      )}
                      <button
                        data-testid={`delete-user-${u.user_id}`}
                        disabled={isSelf || busy === u.user_id}
                        onClick={() => removeUser(u.user_id, u.name)}
                        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-primary-accent disabled:opacity-40 disabled:cursor-not-allowed"
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-500 mt-3">
        Note: You can&apos;t demote or delete yourself, and the last remaining admin is always protected.
      </div>
    </div>
  );
}


function IntegrationsTab({ tournament, refresh }) {
  const [form, setForm] = useState({
    external_provider: tournament.external_provider || "",
    external_competition_code: tournament.external_competition_code || "",
    external_season: tournament.external_season || "",
    sync_enabled: !!tournament.sync_enabled,
  });
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const load = () => api.get(`/admin/sync/${tournament.tournament_id}/status`).then((r) => setStatus(r.data)).catch(() => {});
    load();
    // refresh status + relative clock so "synced X ago" stays current
    const id = setInterval(() => { load(); setNowTs(Date.now()); }, 30000);
    return () => clearInterval(id);
  }, [tournament.tournament_id]);

  const syncAgeMs = status?.last_synced_at ? nowTs - new Date(status.last_synced_at).getTime() : null;
  const timeAgo = (ms) => {
    if (ms == null) return "never";
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };
  // green if synced in last 10 min, amber under 30, red/zinc otherwise
  const syncHealth = syncAgeMs == null ? "off" : syncAgeMs < 10 * 60000 ? "fresh" : syncAgeMs < 30 * 60000 ? "stale" : "old";
  const healthColor = { fresh: "bg-green-500", stale: "bg-amber-500", old: "bg-primary-accent", off: "bg-zinc-600" }[syncHealth];
  const healthText = { fresh: "text-green-400", stale: "text-amber-400", old: "text-primary-accent", off: "text-zinc-500" }[syncHealth];

  const saveConfig = async () => {
    try {
      await api.patch(`/tournaments/${tournament.tournament_id}`, {
        external_provider: form.external_provider || null,
        external_competition_code: form.external_competition_code || null,
        external_season: form.external_season ? parseInt(form.external_season, 10) : null,
        sync_enabled: !!form.sync_enabled,
      });
      await refresh();
      const { data } = await api.get(`/admin/sync/${tournament.tournament_id}/status`);
      setStatus(data);
      toast.success("Integration config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const syncNow = async () => {
    if (!form.external_provider || !form.external_competition_code || !form.external_season) {
      toast.error("Save provider + competition code + season first");
      return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post(`/admin/sync/${tournament.tournament_id}`);
      setLastResult(data);
      const { data: st } = await api.get(`/admin/sync/${tournament.tournament_id}/status`);
      setStatus(st);
      toast.success(`Synced — ${data.created} new, ${data.updated} updated, ${data.auto_settled} settled`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="surface-card p-6 mb-6 accent-bar">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display text-2xl uppercase">External Data Sync</h3>
          <span className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest ${healthText} whitespace-nowrap`} data-testid="sync-last-indicator">
            <span className="relative flex h-2.5 w-2.5">
              {syncHealth === "fresh" && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${healthColor} opacity-75`}></span>}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${healthColor}`}></span>
            </span>
            Synced {timeAgo(syncAgeMs)}
          </span>
        </div>
        <p className="text-sm text-zinc-400 mb-5">
          Pull fixtures, statuses, and results from a 3rd-party provider. Auto-settles predictions on match completion.
          Polls every 15 seconds for fast live updates. Manual admin overrides on the Matches tab still take precedence.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-tag block mb-1">Provider</label>
            <select
              data-testid="ext-provider"
              value={form.external_provider}
              onChange={(e) => setForm({ ...form, external_provider: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
            >
              <option value="">None (manual)</option>
              <option value="football-data.org">football-data.org</option>
            </select>
          </div>
          <div>
            <label className="label-tag block mb-1">Competition Code</label>
            <input
              data-testid="ext-code"
              value={form.external_competition_code}
              onChange={(e) => setForm({ ...form, external_competition_code: e.target.value.toUpperCase() })}
              placeholder="WC, PL, CL, ..."
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
            />
            <div className="text-xs text-zinc-500 mt-1">WC = FIFA World Cup · PL = Premier League · CL = UEFA Champions League</div>
          </div>
          <div>
            <label className="label-tag block mb-1">Season (year)</label>
            <input
              data-testid="ext-season"
              type="number"
              value={form.external_season}
              onChange={(e) => setForm({ ...form, external_season: e.target.value })}
              placeholder="2026"
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
            />
          </div>
          <div>
            <label className="label-tag block mb-1">Auto-Sync (every 15s)</label>
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input
                data-testid="ext-enable"
                type="checkbox"
                checked={form.sync_enabled}
                onChange={(e) => setForm({ ...form, sync_enabled: e.target.checked })}
              />
              <span className={form.sync_enabled ? "text-primary-accent uppercase tracking-widest text-xs" : "text-zinc-500 uppercase tracking-widest text-xs"}>
                {form.sync_enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button data-testid="save-integration" onClick={saveConfig} className="bg-primary-accent text-white px-6 py-2 text-xs uppercase tracking-widest">Save Config</button>
          <button data-testid="sync-now" onClick={syncNow} disabled={syncing} className="border border-primary-accent text-primary-accent px-6 py-2 text-xs uppercase tracking-widest hover:bg-primary-accent hover:text-white disabled:opacity-50">
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
          <button
            data-testid="replace-with-api"
            onClick={async () => {
              if (!form.external_provider) { toast.error("Configure provider first"); return; }
              if (!window.confirm("This will DELETE all manually-created matches (those not from the API) along with their predictions, survivor picks, and chat messages. Continue?")) return;
              setSyncing(true);
              try {
                const { data } = await api.post(`/admin/sync/${tournament.tournament_id}/replace`);
                setLastResult(data);
                const { data: st } = await api.get(`/admin/sync/${tournament.tournament_id}/status`);
                setStatus(st);
                toast.success(`Replaced — removed ${data.deleted_matches} matches, kept/added ${data.fetched} from API`);
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              finally { setSyncing(false); }
            }}
            disabled={syncing}
            className="border border-zinc-700 text-zinc-300 px-6 py-2 text-xs uppercase tracking-widest hover:border-primary-accent hover:text-primary-accent disabled:opacity-50"
          >
            Replace All with API
          </button>
        </div>
      </div>

      <div className="surface-card p-6">
        <h3 className="font-display text-2xl uppercase mb-3">Sync Status</h3>
        {!status?.last_synced_at && <div className="text-zinc-500 text-sm">No syncs yet.</div>}
        {status?.last_synced_at && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div><div className="label-tag">Last Sync</div><div className="text-sm">{new Date(status.last_synced_at).toLocaleString()}</div></div>
            <div><div className="label-tag">Fetched</div><div className="font-display text-2xl">{status.last_sync_summary?.fetched ?? 0}</div></div>
            <div><div className="label-tag">Created</div><div className="font-display text-2xl text-primary-accent">{status.last_sync_summary?.created ?? 0}</div></div>
            <div><div className="label-tag">Auto-Settled</div><div className="font-display text-2xl text-primary-accent">{status.last_sync_summary?.auto_settled ?? 0}</div></div>
            <div data-testid="sync-live-count">
              <div className="label-tag flex items-center gap-1.5">
                {(status.live_count ?? 0) > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/>}
                Live
              </div>
              <div className={`font-display text-2xl ${(status.live_count ?? 0) > 0 ? "text-green-400" : "text-zinc-500"}`}>{status.live_count ?? 0}</div>
            </div>
          </div>
        )}
        {(lastResult?.errors?.length || status?.last_sync_summary?.errors?.length) > 0 && (
          <div className="mt-4">
            <div className="label-tag mb-2">Errors</div>
            <div className="bg-black/40 border border-primary-accent/30 p-3 text-xs text-primary-accent font-mono space-y-1 max-h-40 overflow-y-auto">
              {(lastResult?.errors || status?.last_sync_summary?.errors || []).map((e, i) => <div key={i}>{e}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ManualTab() {
  return (
    <div className="max-w-4xl space-y-4">
      <div className="surface-card p-6 accent-bar">
        <h3 className="font-display text-2xl uppercase">Admin Manual</h3>
        <p className="text-sm text-zinc-400 mt-2">
          Everything an admin can do, and the path to do it. New admin? Start here. Bookmark this tab — it grows whenever
          features are added.
        </p>
      </div>

      <ManualSection title="Quickstart Checklist" testid="manual-quickstart">
        <Numbered items={[
          <>Confirm the active <strong>Tournament</strong> in the top-left switcher.</>,
          <>Open <em>Competitions</em> tab — toggle which of the 5 competitions apply (e.g. disable Survivor for an IPL tournament).</>,
          <>Open <em>Scoring</em> tab — set points, multipliers, fantasy budget, group method and (if needed) lock group affiliations.</>,
          <>Open <em>Integrations</em> tab — if using football-data.org, pick provider + competition code + season, then click <em>Sync Now</em> (or <em>Replace All with API</em> to wipe manual matches first).</>,
          <>Open <em>Matches</em> tab — confirm the schedule, set matchday numbers if not auto-imported, and use <em>Go Live</em> as a manual fallback if the provider lags.</>,
          <>Open <em>Groups</em> tab — create/edit social groups (e.g. &quot;Arsenal Supporters&quot;) that users can affiliate with.</>,
          <>Promote co-admins via <em>Users</em> tab when you need help.</>,
        ]}/>
      </ManualSection>

      <ManualSection title="Overview Tab" testid="manual-overview">
        <Para>At-a-glance counters: Users, Predictions, Settled, Matches, Completed. Refreshes when you switch tournaments.</Para>
      </ManualSection>

      <ManualSection title="Users Tab" testid="manual-users">
        <Para>Search by name/email, filter by role, see predictions & points per user.</Para>
        <Bullets items={[
          <><Strong>Promote</Strong> any participant to admin · <Strong>Demote</Strong> any admin back to participant.</>,
          <><Strong>Delete</Strong> a user — also revokes their sessions and removes their profile.</>,
          <>Safety: you can&apos;t demote/delete <em>yourself</em>, and the system never lets you remove the last remaining admin.</>,
        ]}/>
      </ManualSection>

      <ManualSection title="Competitions Tab" testid="manual-competitions">
        <Para>Toggle which of the 5 competitions are enabled for the active tournament:</Para>
        <Bullets items={[
          <><Strong>Main League</Strong> — scoreline predictions</>,
          <><Strong>Survivor</Strong> — pick-one-team-per-matchday</>,
          <><Strong>Bracket</Strong> — full knockout bracket</>,
          <><Strong>Fantasy XI</Strong> — 11-player squad builder</>,
          <><Strong>Streak</Strong> — consecutive-correct-results counter</>,
          <><Strong>Bonus Questions</Strong> — tournament-long predictions (Golden Boot, winner, etc.)</>,
          <><Strong>Chat Room</Strong> — reddit-style tournament & group chat with up/down votes</>,
        ]}/>
        <Para>Disabled competitions disappear from the participant top-nav and rules page automatically.</Para>
      </ManualSection>

      <ManualSection title="Scoring Tab" testid="manual-scoring">
        <Para>Every numeric points value users earn is editable here:</Para>
        <Bullets items={[
          <><Strong>Main League</Strong>: correct result / GD / exact / underdog bonus + underdog threshold</>,
          <><Strong>Bracket</Strong>: per-round point values (R32 → Final)</>,
          <><Strong>Fantasy</Strong>: budget cap, max-per-nation, goal/assist/CS/save/yellow/red values</>,
          <><Strong>Multipliers</Strong>: Captain (default 2×), Power Play (default 3×)</>,
          <><Strong>Groups</Strong>: scoring method (top_3 / top_5 / sum / average) + affiliations-locked toggle</>,
        ]}/>
        <Para>Changes are visible to participants on the <em>Rules</em> page immediately after saving.</Para>
      </ManualSection>

      <ManualSection title="Matches Tab" testid="manual-matches">
        <Para>The schedule editor. Each row supports:</Para>
        <Bullets items={[
          <><Strong>Matchday</Strong>: inline number input — saves on blur or Enter.</>,
          <><Strong>Status</Strong>: <em>Scheduled</em>, <em>Live</em>, or <em>FT</em>. Click <em>Go Live</em> to force a match live (used as a fallback when the API lags). <em>End Live</em> reverts it.</>,
          <><Strong>Score + Settle</Strong>: enter home–away and click <em>Settle</em>. This locks the score, marks <em>FT</em>, awards all main-league + survivor points, and updates leaderboards.</>,
        ]}/>
        <Para className="text-zinc-500 text-xs">Manual settles are protected — even if the provider re-syncs, your manual scores are never overwritten.</Para>
      </ManualSection>

      <ManualSection title="Groups Tab" testid="manual-groups">
        <Para>Manage the social groups participants can represent (e.g. &quot;Liverpool Supporters&quot;, &quot;Office Bangalore&quot;).</Para>
        <Bullets items={[
          <><Strong>Create</Strong>: name + description + color + optional logo URL.</>,
          <><Strong>Edit</Strong>: change any field inline.</>,
          <><Strong>Archive</Strong> hides a group from public listing without losing members.</>,
          <><Strong>Delete</Strong>: only works on empty groups; groups with members auto-archive instead.</>,
        ]}/>
      </ManualSection>

      <ManualSection title="Integrations Tab" testid="manual-integrations">
        <Para>Connect a 3rd-party data provider so fixtures, statuses and results sync automatically.</Para>
        <Bullets items={[
          <><Strong>Provider</Strong>: football-data.org (more can be added later).</>,
          <><Strong>Competition Code</Strong>: <em>WC</em> for FIFA World Cup, <em>PL</em> for Premier League, <em>CL</em> for UEFA Champions League, etc.</>,
          <><Strong>Season</Strong>: 4-digit year.</>,
          <><Strong>Auto-Sync</Strong>: when on, the background poller pulls updates every 15 seconds for live scores.</>,
          <><Strong>Sync Now</Strong>: pulls immediately. Upserts by external id — re-syncs are idempotent.</>,
          <><Strong>Replace All with API</Strong>: nukes any manually-created matches (and their predictions / chat / survivor picks) and re-syncs from scratch. Use when bootstrapping a new tournament.</>,
        ]}/>
        <Para>When a match flips to FINISHED on the provider, the sync auto-settles predictions exactly like the manual <em>Settle</em> button.</Para>
      </ManualSection>

      <ManualSection title="Tournaments Tab" testid="manual-tournaments">
        <Para>List all tournaments, switch the active one, or create a new one (Premier League, IPL, UCL, whatever).</Para>
        <Para>Each new tournament starts fresh with default config + competition toggles you can customize. Sport selector exists for future cricket/basketball modules.</Para>
      </ManualSection>

      <ManualSection title="Common Workflows" testid="manual-workflows">
        <h4 className="font-display text-base uppercase mt-2 mb-1">Settling a match (provider down)</h4>
        <Numbered items={[
          <>Go to <em>Matches</em></>,
          <>Enter home & away score in the row</>,
          <>Click <em>Settle</em> — predictions auto-scored, leaderboards updated</>,
        ]}/>
        <h4 className="font-display text-base uppercase mt-4 mb-1">Adding a new tournament</h4>
        <Numbered items={[
          <>Open <em>Tournaments</em> tab → fill name / short code / sport → <em>Create</em></>,
          <>Switch active to the new tournament (top-left switcher)</>,
          <>Set <em>Competitions</em> toggles, <em>Scoring</em>, then <em>Integrations</em> to import fixtures</>,
          <>(Optional) Create starter <em>Groups</em></>,
          <>Announce to participants — they&apos;ll see the new tournament in the switcher and the Rules page updates automatically</>,
        ]}/>
        <h4 className="font-display text-base uppercase mt-4 mb-1">Promoting a co-admin</h4>
        <Numbered items={[
          <>Open <em>Users</em> tab</>,
          <>Search the email/name</>,
          <>Click <em>Promote</em> on their row</>,
        ]}/>
      </ManualSection>

      <ManualSection title="Things Admins Cannot (currently) Do" testid="manual-cant-do">
        <Bullets items={[
          <>Edit individual predictions on behalf of a user</>,
          <>Bulk-import users from CSV</>,
          <>Send notifications/emails to participants (planned)</>,
          <>Disable a user without deleting them</>,
          <>Manual prize-pool / winners view (planned)</>,
        ]}/>
      </ManualSection>
    </div>
  );
}

const ManualSection = ({ title, children, testid }) => (
  <section className="surface-card p-5" data-testid={testid}>
    <h3 className="font-display text-xl uppercase mb-3 text-primary-accent">{title}</h3>
    <div className="space-y-2 text-sm text-zinc-300">{children}</div>
  </section>
);

const Para = ({ children, className = "" }) => <p className={className}>{children}</p>;
const Strong = ({ children }) => <span className="text-white font-display tracking-wider">{children}</span>;
const Bullets = ({ items }) => (
  <ul className="list-disc list-outside ml-5 space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
const Numbered = ({ items }) => (
  <ol className="list-decimal list-outside ml-5 space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ol>
);


function FeedbackTab() {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const params = {};
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/admin/feedback", { params });
    setData(data);
  };

  useEffect(() => { load();   }, [typeFilter, statusFilter]);

  const updateStatus = async (fid, status) => {
    try {
      await api.patch(`/admin/feedback/${fid}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/admin/feedback/${fid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const typeIcon = (t) => t === "bug" ? "[BUG]" : t === "feature" ? "[FEAT]" : "[OTHER]";

  const STATUS_OPTS = ["open", "in_progress", "done", "wont_fix"];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-2xl uppercase">User Feedback ({data.total})</h3>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="fb-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-xs uppercase tracking-widest focus:border-primary-accent focus:outline-none">
            <option value="">All Types</option>
            <option value="feature">Features</option>
            <option value="bug">Bugs</option>
            <option value="other">Other</option>
          </select>
          <select data-testid="fb-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-xs uppercase tracking-widest focus:border-primary-accent focus:outline-none">
            <option value="">All Status</option>
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUS_OPTS.map((s) => (
          <div key={s} className="surface-card px-3 py-1.5 text-xs uppercase tracking-widest">
            <span className="text-zinc-500 mr-2">{s.replace("_", " ")}</span>
            <span className="font-display text-base text-primary-accent">{data.counts[s] || 0}</span>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {data.items.length === 0 && (
          <div className="surface-card p-6 text-center text-zinc-500">No feedback matches this filter.</div>
        )}
        {data.items.map((f) => (
          <div key={f.feedback_id} className="surface-card p-4" data-testid={`fb-${f.feedback_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 border ${
                    f.type === "bug" ? "border-primary-accent text-primary-accent" : f.type === "feature" ? "border-green-500/50 text-green-400" : "border-zinc-700 text-zinc-400"
                  }`}>{typeIcon(f.type)}</span>
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 ${
                    f.status === "open" ? "bg-zinc-800 text-zinc-300" :
                    f.status === "in_progress" ? "bg-yellow-500/20 text-yellow-300" :
                    f.status === "done" ? "bg-green-500/20 text-green-400" :
                    "bg-zinc-900 text-zinc-600"
                  }`}>{f.status.replace("_", " ")}</span>
                  <span className="text-xs text-zinc-500">
                    {f.user_name} · {f.user_email} · {new Date(f.created_at).toLocaleString()} · {f.page || "—"}
                  </span>
                </div>
                <div className="font-display text-lg">{f.title}</div>
                {f.description && <p className="text-sm text-zinc-400 mt-1 whitespace-pre-wrap">{f.description}</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_OPTS.filter((s) => s !== f.status).map((s) => (
                  <button
                    key={s}
                    data-testid={`fb-set-${s}-${f.feedback_id}`}
                    onClick={() => updateStatus(f.feedback_id, s)}
                    className="text-xs uppercase tracking-widest border border-zinc-800 px-2 py-1 hover:border-primary-accent hover:text-primary-accent"
                  >{s.replace("_", " ")}</button>
                ))}
                <button
                  data-testid={`fb-delete-${f.feedback_id}`}
                  onClick={() => remove(f.feedback_id)}
                  className="text-xs uppercase tracking-widest text-zinc-500 hover:text-primary-accent"
                >Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

